import React, { useState, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  VStack,
  Grid,
  GridItem,
  Select,
  Spinner,
  Text,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';

import { useQuery } from 'react-query';
import { getConsultants } from '../../services/consultantService';

/**
 * فرم افزودن/ویرایش مراجع
 * @param {Object} props - props کامپوننت
 * @param {Object} props.client - اطلاعات مراجع برای ویرایش (اختیاری)
 * @param {Function} props.onSubmit - تابع ارسال فرم
 * @param {boolean} props.isLoading - وضعیت بارگذاری
 */
const ClientForm = React.forwardRef(({ client, onSubmit, isLoading }, ref) => {

  // دریافت لیست مشاوران
  const { 
    data: consultantsData, 
    isLoading: consultantsLoading, 
    error: consultantsError 
  } = useQuery(['consultants'], getConsultants, {
    staleTime: 5 * 60 * 1000, // 5 دقیقه
    retry: 2,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    getValues,
  } = useForm({
    defaultValues: {
      firstName: client?.firstName || '',
      lastName: client?.lastName || '',
      nationalId: client?.nationalId || '',
      phone: client?.phone || '',
      email: client?.email || '',
      address: client?.address || '',
      occupation: client?.occupation || '',
      birthYear: client?.birthDate ? client.birthDate.split('/')[0] : '',
      birthMonth: client?.birthDate ? client.birthDate.split('/')[1] : '',
      birthDay: client?.birthDate ? client.birthDate.split('/')[2] : '',
      consultant: client?.consultant?._id || client?.consultant || '',
      notes: client?.notes || '',
      emergencyContact: client?.emergencyContact || '',
      emergencyPhone: client?.emergencyPhone || '',
    },
    mode: 'onChange', // اعتبارسنجی در حین تایپ
  });





  const handleFormSubmit = (data) => {
    // بررسی وجود فیلدهای تاریخ تولد
    if (!data.birthYear || !data.birthMonth || !data.birthDay) {
      throw new Error('تمام فیلدهای تاریخ تولد الزامی است');
    }
    
    // ترکیب سه فیلد تاریخ تولد
    const birthYear = data.birthYear.toString().trim();
    const birthMonth = data.birthMonth.toString().trim().padStart(2, '0');
    const birthDay = data.birthDay.toString().trim().padStart(2, '0');
    
    // بررسی اینکه فیلدها خالی نباشند
    if (!birthYear || !birthMonth || !birthDay) {
      throw new Error('تمام فیلدهای تاریخ تولد الزامی است');
    }
    const birthDate = `${birthYear}/${birthMonth}/${birthDay}`;
    
    // اعتبارسنجی تاریخ تولد
    const year = parseInt(birthYear);
    const month = parseInt(birthMonth);
    const day = parseInt(birthDay);
    
    // بررسی اعتبار تاریخ شمسی (ساده)
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      throw new Error('تاریخ تولد وارد شده معتبر نیست');
    }
    
    // بررسی اینکه سال معقول باشد (بین 1300 تا 1410)
    if (year < 1300 || year > 1410) {
      throw new Error('سال تولد باید بین 1300 تا 1410 باشد');
    }
    
    // بررسی اینکه تاریخ در آینده نباشد (تقریبی)
    const currentYear = new Date().getFullYear();
    const gregorianYear = year + 621;
    if (gregorianYear > currentYear) {
      throw new Error('تاریخ تولد نمی‌تواند در آینده باشد');
    }
    
    // تنظیم مقادیر پیش‌فرض برای فیلدهای اختیاری
    const cleanData = {
      ...data,
      occupation: data.occupation || 'ثبت نشده',
      birthDate: birthDate,
      address: data.address || 'ثبت نشده',
      email: data.email || '',
      emergencyContact: data.emergencyContact || '',
      emergencyPhone: data.emergencyPhone || '',
      notes: data.notes || ''
    };
    
    // حذف فیلدهای جداگانه از cleanData
    delete cleanData.birthYear;
    delete cleanData.birthMonth;
    delete cleanData.birthDay;
    
    onSubmit(cleanData);
  };

  // استفاده از useImperativeHandle برای پاس دادن توابع به parent
  useImperativeHandle(ref, () => ({
    getValues: () => getValues(),
    submit: () => {
      trigger().then((isValid) => {
        if (isValid) {
          handleSubmit(handleFormSubmit)();
        }
      });
    }
  }));

  // دریافت لیست مشاوران
  const consultants = consultantsData?.data?.consultants || [];

  // محاسبه تعداد فیلدهای پر شده
  const filledFields = Object.keys(watch()).filter(field => {
    const value = watch(field);
    return value && value.toString().trim() !== '';
  }).length;

  const totalRequiredFields = 8; // نام، نام خانوادگی، کد ملی، مشاور، شماره تماس، سال، ماه، روز
  const completionPercentage = Math.round((filledFields / totalRequiredFields) * 100);

  return (
    <form ref={ref} onSubmit={handleSubmit(handleFormSubmit)}>
      <VStack spacing={4} align="stretch">
        {/* نمایش خطاهای کلی فرم */}
        {Object.keys(errors).length > 0 && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <VStack align="start" spacing={1}>
              <Text fontWeight="bold">لطفاً خطاهای زیر را برطرف کنید:</Text>
              {Object.entries(errors).map(([fieldName, error]) => (
                <Text key={fieldName} fontSize="sm">
                  • {error.message}
                </Text>
              ))}
            </VStack>
          </Alert>
        )}

        {/* نمایش پیش‌نمایش اطلاعات */}
        {Object.keys(errors).length === 0 && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <VStack align="start" spacing={1}>
              <Text fontSize="sm">
                تمام فیلدهای الزامی پر شده‌اند. می‌توانید فرم را ثبت کنید.
              </Text>
              <Text fontSize="xs" color="gray.600">
                پیشرفت: {filledFields} از {totalRequiredFields} فیلد الزامی ({completionPercentage}%)
              </Text>
            </VStack>
          </Alert>
        )}
        {/* نام و نام خانوادگی */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isInvalid={errors.firstName}>
              <FormLabel>نام *</FormLabel>
              <Input
                {...register('firstName', {
                  required: 'نام الزامی است',
                  minLength: { value: 2, message: 'نام باید حداقل 2 کاراکتر باشد' },
                  maxLength: { value: 50, message: 'نام نمی‌تواند بیشتر از 50 کاراکتر باشد' },
                  pattern: {
                    value: /^[\u0600-\u06FF\s]+$/,
                    message: 'نام باید فقط شامل حروف فارسی باشد'
                  },
                  validate: (value) => {
                    if (value && value.trim().length < 2) {
                      return 'نام باید حداقل 2 کاراکتر باشد';
                    }
                    return true;
                  }
                })}
                placeholder="نام مراجع"
              />
              <FormErrorMessage>
                {errors.firstName && errors.firstName.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.lastName}>
              <FormLabel>نام خانوادگی *</FormLabel>
              <Input
                {...register('lastName', {
                  required: 'نام خانوادگی الزامی است',
                  minLength: { value: 2, message: 'نام خانوادگی باید حداقل 2 کاراکتر باشد' },
                  maxLength: { value: 50, message: 'نام خانوادگی نمی‌تواند بیشتر از 50 کاراکتر باشد' },
                  pattern: {
                    value: /^[\u0600-\u06FF\s]+$/,
                    message: 'نام خانوادگی باید فقط شامل حروف فارسی باشد'
                  },
                  validate: (value) => {
                    if (value && value.trim().length < 2) {
                      return 'نام خانوادگی باید حداقل 2 کاراکتر باشد';
                    }
                    return true;
                  }
                })}
                placeholder="نام خانوادگی مراجع"
              />
              <FormErrorMessage>
                {errors.lastName && errors.lastName.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>

        {/* کد ملی و مشاور */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isInvalid={errors.nationalId}>
              <FormLabel>کد ملی *</FormLabel>
              <Input
                {...register('nationalId', {
                  required: 'کد ملی الزامی است',
                  pattern: {
                    value: /^\d{10}$/,
                    message: 'کد ملی باید دقیقاً 10 رقم باشد',
                  },
                  validate: (value) => {
                    if (!value) return 'کد ملی الزامی است';
                    if (!/^\d{10}$/.test(value)) {
                      return 'کد ملی باید دقیقاً 10 رقم باشد';
                    }
                    // بررسی اعتبار کد ملی (الگوریتم ساده)
                    const digits = value.split('').map(Number);
                    const checkDigit = digits[9];
                    const sum = digits.slice(0, 9).reduce((acc, digit, index) => {
                      return acc + (digit * (10 - index));
                    }, 0);
                    const remainder = sum % 11;
                    const calculatedCheckDigit = remainder < 2 ? remainder : 11 - remainder;
                    
                    if (checkDigit !== calculatedCheckDigit) {
                      return 'کد ملی وارد شده معتبر نیست';
                    }
                    return true;
                  }
                })}
                placeholder="1234567890"
                maxLength={10}
              />
              <FormErrorMessage>
                {errors.nationalId && errors.nationalId.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.consultant}>
              <FormLabel>مشاور *</FormLabel>
              {consultantsLoading ? (
                <VStack spacing={2} align="start">
                  <Spinner size="sm" color="teal.500" />
                  <Text fontSize="sm" color="gray.500">در حال بارگذاری مشاوران...</Text>
                </VStack>
              ) : consultantsError ? (
                <Text fontSize="sm" color="red.500">
                  خطا در بارگذاری مشاوران: {consultantsError.message}
                </Text>
              ) : (
                <Select
                  {...register('consultant', {
                    required: 'انتخاب مشاور الزامی است',
                  })}
                  placeholder="مشاور را انتخاب کنید"
                  isDisabled={isLoading || consultantsLoading}
                >
                  {consultants.map((consultant) => (
                    <option key={consultant._id} value={consultant._id}>
                      {consultant.name}
                    </option>
                  ))}
                </Select>
              )}
              <FormErrorMessage>
                {errors.consultant && errors.consultant.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>

        {/* شماره تماس و ایمیل */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isInvalid={errors.phone}>
              <FormLabel>شماره تماس *</FormLabel>
              <Input
                {...register('phone', {
                  required: 'شماره تماس الزامی است',
                  pattern: {
                    value: /^09\d{9}$/,
                    message: 'شماره تماس باید با 09 شروع شود و 11 رقم باشد',
                  },
                  validate: (value) => {
                    if (!value) return 'شماره تماس الزامی است';
                    if (!/^09\d{9}$/.test(value)) {
                      return 'شماره تماس باید با 09 شروع شود و 11 رقم باشد';
                    }
                    // بررسی اینکه شماره تکراری نباشد (در صورت نیاز)
                    return true;
                  }
                })}
                placeholder="09120000000"
              />
              <FormErrorMessage>
                {errors.phone && errors.phone.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.email}>
              <FormLabel>ایمیل</FormLabel>
              <Input
                {...register('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'ایمیل معتبر نیست',
                  },
                  validate: (value) => {
                    if (value && value.trim() !== '') {
                      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                      if (!emailRegex.test(value)) {
                        return 'ایمیل معتبر نیست';
                      }
                      // بررسی دامنه‌های معتبر (اختیاری)
                      const domain = value.split('@')[1];
                      const invalidDomains = ['test.com', 'example.com', 'temp.com'];
                      if (invalidDomains.includes(domain)) {
                        return 'لطفاً ایمیل معتبر وارد کنید';
                      }
                    }
                    return true;
                  }
                })}
                placeholder="example@email.com"
              />
              <FormErrorMessage>
                {errors.email && errors.email.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>



        {/* آدرس و یادداشت‌ها */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isInvalid={errors.address}>
              <FormLabel>آدرس</FormLabel>
              <Textarea
                {...register('address')}
                placeholder="آدرس کامل مراجع"
                rows={3}
              />
              <FormErrorMessage>
                {errors.address && errors.address.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.notes}>
              <FormLabel>یادداشت‌ها</FormLabel>
              <Textarea
                {...register('notes')}
                placeholder="یادداشت‌های مربوط به مراجع"
                rows={3}
              />
              <FormErrorMessage>
                {errors.notes && errors.notes.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>

        {/* شغل و تاریخ تولد */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isInvalid={errors.occupation}>
              <FormLabel>شغل</FormLabel>
              <Input
                {...register('occupation')}
                placeholder="شغل مراجع"
              />
              <FormErrorMessage>
                {errors.occupation && errors.occupation.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.birthDate}>
              <FormLabel>تاریخ تولد *</FormLabel>
              <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                <GridItem>
                  <Input
                    {...register('birthYear', {
                      required: 'سال تولد الزامی است',
                      min: { value: 1300, message: 'سال باید بعد از 1300 باشد' },
                      max: { value: 1410, message: 'سال باید قبل از 1410 باشد' },
                      validate: (value) => {
                        if (!value || value.toString().trim() === '') return 'سال تولد الزامی است';
                        const year = parseInt(value);
                        if (isNaN(year) || year < 1300 || year > 1410) {
                          return 'سال تولد باید بین 1300 تا 1410 باشد';
                        }
                        return true;
                      }
                    })}
                    placeholder="سال"
                    type="number"
                    min="1300"
                    max="1410"
                  />
                </GridItem>
                <GridItem>
                  <Input
                    {...register('birthMonth', {
                      required: 'ماه تولد الزامی است',
                      min: { value: 1, message: 'ماه باید بین 1 تا 12 باشد' },
                      max: { value: 12, message: 'ماه باید بین 1 تا 12 باشد' },
                      validate: (value) => {
                        if (!value || value.toString().trim() === '') return 'ماه تولد الزامی است';
                        const month = parseInt(value);
                        if (isNaN(month) || month < 1 || month > 12) {
                          return 'ماه تولد باید بین 1 تا 12 باشد';
                        }
                        return true;
                      }
                    })}
                    placeholder="ماه"
                    type="number"
                    min="1"
                    max="12"
                  />
                </GridItem>
                <GridItem>
                  <Input
                    {...register('birthDay', {
                      required: 'روز تولد الزامی است',
                      min: { value: 1, message: 'روز باید بین 1 تا 31 باشد' },
                      max: { value: 31, message: 'روز باید بین 1 تا 31 باشد' },
                      validate: (value) => {
                        if (!value || value.toString().trim() === '') return 'روز تولد الزامی است';
                        const day = parseInt(value);
                        if (isNaN(day) || day < 1 || day > 31) {
                          return 'روز تولد باید بین 1 تا 31 باشد';
                        }
                        return true;
                      }
                    })}
                    placeholder="روز"
                    type="number"
                    min="1"
                    max="31"
                  />
                </GridItem>
              </Grid>
              <FormErrorMessage>
                {errors.birthDate && errors.birthDate.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>

        {/* تماس اضطراری */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isInvalid={errors.emergencyContact}>
              <FormLabel>تماس اضطراری</FormLabel>
              <Input
                {...register('emergencyContact')}
                placeholder="نام تماس اضطراری"
              />
              <FormErrorMessage>
                {errors.emergencyContact && errors.emergencyContact.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.emergencyPhone}>
              <FormLabel>شماره تماس اضطراری</FormLabel>
              <Input
                {...register('emergencyPhone', {
                  validate: (value) => {
                    // اگر فیلد خالی باشد، validation نکن
                    if (!value || value.trim() === '') {
                      return true;
                    }
                    
                    // اگر مقداری وارد شده، validation کن
                    if (!/^09\d{9}$/.test(value)) {
                      return 'شماره تماس باید با 09 شروع شود و 11 رقم باشد';
                    }
                    
                    // بررسی اینکه شماره با شماره اصلی متفاوت باشد
                    const mainPhone = watch('phone');
                    if (value === mainPhone) {
                      return 'شماره تماس اضطراری باید با شماره اصلی متفاوت باشد';
                    }
                    
                    return true;
                  }
                })}
                placeholder="09120000000 (اختیاری)"
              />
              <FormErrorMessage>
                {errors.emergencyPhone && errors.emergencyPhone.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>


      </VStack>
    </form>
  );
});

export default ClientForm; 